import {
  faPen,
  faPlus,
  faSearch,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EmptyList from 'components/common/EmptyList';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import Pagination from 'components/pagination';
import { useDebounce } from 'helpers/utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { useFormatDateTime } from 'hooks/useFormatDateTime';
import useNotification from 'hooks/useNotification';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  ButtonGroup,
  Col,
  FormControl,
  Row
} from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import MessageTemplateService from 'services/MessageTemplate.service';
import MessageTemplateDeleteModal from './MessageTemplateDeleteModal';
import MessageTemplateModal from './MessageTemplateModal';

const MessageTemplates = () => {
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const [templates, setTemplates] = useState({
    content: [],
    totalElements: 0
  });

  const [filters, setFilters] = useState({
    page: 0,
    size: 20,
    name: ''
  });

  const [searchTerm, setSearchTerm] = useState('');

  const getData = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams();

      if (filters.page !== undefined) {
        queryParams.append('page', filters.page);
      }
      if (filters.size !== undefined) {
        queryParams.append('size', filters.size);
      }
      if (filters.name) {
        queryParams.append('name', filters.name);
      }

      const response = await MessageTemplateService.list(
        queryParams.toString()
      );

      if (response.content) {
        setTemplates(response);
      }
    } catch (error) {
      console.error('Erro ao buscar modelos de mensagens:', error);
      showErrorNotification('Erro ao carregar modelos de mensagens');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [filters]);

  const getTypeBadgeColor = type => {
    const colors = {
      NOTE: 'info',
      EMAIL: 'primary',
      APPOINTMENT: 'warning',
      TICKET_DESCRIPTION: 'success'
    };
    return colors[type] || 'secondary';
  };

  const getTypeLabel = type => {
    const labels = {
      NOTE: 'Nota',
      EMAIL: 'E-mail',
      APPOINTMENT: 'Apontamento',
      TICKET_DESCRIPTION: 'Descrição'
    };
    return labels[type] || type;
  };

  const columns = [
    {
      accessorKey: 'name',
      header: 'Nome',
      meta: {
        headerProps: { className: 'text-900 w-30' }
      },
      cell: rowData => {
        const { name } = rowData.row.original;
        return <div>{name}</div>;
      }
    },
    {
      accessorKey: 'types',
      header: 'Tipos',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { types } = rowData.row.original;
        if (!types || types.length === 0) {
          return <div>-</div>;
        }
        return (
          <div className="d-flex flex-wrap gap-1">
            {types.map(type => (
              <Badge key={type} bg={getTypeBadgeColor(type)} className="fs-11">
                {getTypeLabel(type)}
              </Badge>
            ))}
          </div>
        );
      }
    },
    {
      accessorKey: 'creationDate',
      header: 'Data de criação',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { creationDate } = rowData.row.original;
        const formatDateTime = useFormatDateTime();
        return <div>{formatDateTime(creationDate)}</div>;
      }
    },
    {
      accessorKey: 'lastUpdateDate',
      header: 'Data de última modificação',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { lastUpdateDate } = rowData.row.original;
        const formatDateTime = useFormatDateTime();
        return <div>{formatDateTime(lastUpdateDate)}</div>;
      }
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const template = rowData.row.original;
        return (
          <ButtonGroup>
            <Button
              variant="link"
              size="sm"
              onClick={() => handleEdit(template)}
            >
              <FontAwesomeIcon icon={faPen} className="text-primary" />
            </Button>
            <Button
              variant="link"
              className="text-danger"
              size="sm"
              onClick={() => handleDelete(template)}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </ButtonGroup>
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: templates.content,
    columns
  });

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
  };

  const handleOpenModal = () => {
    setEditingTemplate(null);
    setShowModal(true);
  };

  const handleEdit = async template => {
    try {
      const response = await MessageTemplateService.findById(template.id);
      if (response.id) {
        setEditingTemplate(response);
        setShowModal(true);
      } else {
        showErrorNotification('Erro ao carregar modelo de mensagem');
      }
    } catch (error) {
      showErrorNotification('Erro ao carregar modelo de mensagem');
    }
  };

  const handleDelete = template => {
    setSelectedTemplate(template);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedTemplate(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTemplate) return;

    try {
      const response = await MessageTemplateService.delete(selectedTemplate.id);
      if (response.status === 204 || !response.userMessage) {
        showSuccessNotification('Modelo de mensagem excluído com sucesso');
        getData();
        handleCloseDeleteModal();
      } else {
        showErrorNotification(response.userMessage || 'Erro ao excluir');
      }
    } catch (error) {
      showErrorNotification('Erro ao excluir modelo de mensagem');
    }
  };

  const handleModalSuccess = () => {
    getData();
    handleCloseModal();
  };

  const debouncedSearch = useDebounce(searchValue => {
    setFilters(prevFilters => ({
      ...prevFilters,
      page: 0,
      name: searchValue
    }));
  }, 400);

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm]);

  const isEmpty = !isLoading && templates.content.length === 0;

  return (
    <>
      <MessageTemplateModal
        showModal={showModal}
        handleClose={handleCloseModal}
        editingTemplate={editingTemplate}
        onSuccess={handleModalSuccess}
      />

      <MessageTemplateDeleteModal
        showModal={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        handleConfirm={handleConfirmDelete}
        template={selectedTemplate}
      />

      <FalconComponentCard>
        <FalconComponentCard.Header title="Modelos de Mensagens">
          <Flex>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleOpenModal}
            >
              <FontAwesomeIcon icon={faPlus} size="sm" className="me-2" />
              Novo
            </Button>
          </Flex>
        </FalconComponentCard.Header>

        <FalconComponentCard.Body className="bg-white">
          <Row className="justify-content-between g-0">
            <Col className="d-flex align-items-center col-auto pe-3 mb-3">
              <div className="position-relative input-search-width">
                <FormControl
                  size="sm"
                  id="search"
                  type="search"
                  placeholder="Buscar por nome..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="rounded-pill ps-4"
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  className="position-absolute text-secondary"
                  style={{
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '12px'
                  }}
                />
              </div>
            </Col>
          </Row>
          {isLoading ? (
            <Skeleton count={10} height={44} baseColor="#5E6E82" />
          ) : isEmpty ? (
            <EmptyList imgSize={500} vhSize={75} />
          ) : (
            <>
              <AdvanceTableProvider {...table}>
                <AdvanceTable
                  headerClassName="text-nowrap align-middle"
                  rowClassName="align-middle white-space-nowrap"
                  tableProps={{
                    striped: true,
                    className: 'fs-10 mb-0 overflow-hidden'
                  }}
                />
              </AdvanceTableProvider>
              <Pagination
                data={templates}
                filters={filters}
                setFilters={setFilters}
              />
            </>
          )}
        </FalconComponentCard.Body>
      </FalconComponentCard>
    </>
  );
};

export default MessageTemplates;
